/**
 * Serialisation with schema versioning and forward migrations.
 *
 * Rule for this project: every schema change ships with a migration and a test
 * that loads a document written by the previous version. The migration chain is
 * a registry keyed by *source* version, so adding v3 means adding one entry.
 *
 * v1 — PoC format: a static board. Entities carried their own `position`, there
 *      was no timeline, and shapes had no time binding.
 * v2 — positions live in keyframes, each keyframe carries an authored `t` and
 *      shapes carry an `appearsAt`/`hidesAt` window.
 * v3 — current: timing is derived from how far the pieces travel, so a phase has
 *      no time, and a shape names the phase it belongs to.
 */

import type { Vec2 } from './geometry.ts';
import { isBoardView } from './view.ts';
import { SCHEMA_VERSION, type Entity, type Keyframe, type PlayDocument, type Shape, type ShapeKind } from './document.ts';

export class SchemaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SchemaError';
  }
}

type Json = Record<string, unknown>;

const SHAPE_KINDS: readonly ShapeKind[] = ['move', 'pass', 'freehand', 'shot'];

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asArray(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) throw new SchemaError(`Expected "${field}" to be an array`);
  return value;
}

function asString(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new SchemaError(`Expected "${field}" to be a string`);
  return value;
}

function asNumber(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new SchemaError(`Expected "${field}" to be a finite number`);
  }
  return value;
}

function asVec(value: unknown, field: string): Vec2 {
  if (!isObject(value)) throw new SchemaError(`Expected "${field}" to be a point`);
  return { x: asNumber(value['x'], `${field}.x`), y: asNumber(value['y'], `${field}.y`) };
}

function asShapeKind(value: unknown, field: string): ShapeKind {
  const kind = asString(value, field);
  // Dribbling is swimming with the ball (#74): a stroke saved as a dribble
  // reads as a swim, so a board drawn before the two tools merged still opens.
  if (kind === 'dribble') return 'move';
  if (!SHAPE_KINDS.includes(kind as ShapeKind)) throw new SchemaError(`Unknown shape kind "${kind}"`);
  return kind as ShapeKind;
}

/* ------------------------------------------------------------------ */
/* Migrations                                                          */
/* ------------------------------------------------------------------ */

type Migration = (input: Json) => Json;

/**
 * v1 -> v2: lift the per-entity `position` into a single keyframe at t=0 and give
 * every shape a time binding. Keyframe ids are derived from the document id so
 * the migration is deterministic and repeatable.
 */
const migrateV1toV2: Migration = (input) => {
  const docId = asString(input['id'], 'id');
  const rawEntities = asArray(input['entities'], 'entities');

  const positions: Record<string, Vec2> = {};
  const entities = rawEntities.map((raw) => {
    if (!isObject(raw)) throw new SchemaError('Expected entity object');
    const id = asString(raw['id'], 'entity.id');
    positions[id] = asVec(raw['position'], 'entity.position');
    const { position: _position, ...rest } = raw;
    return rest;
  });

  const shapes = asArray(input['shapes'] ?? [], 'shapes').map((raw) => {
    if (!isObject(raw)) throw new SchemaError('Expected shape object');
    return { ...raw, appearsAt: 0 };
  });

  return {
    ...input,
    schemaVersion: 2,
    entities,
    shapes,
    keyframes: [{ id: `${docId}_kf0`, t: 0, positions }],
  };
};

/**
 * v2 -> v3: timing leaves the document (it is derived from how far pieces
 * travel now) and a drawing names the phase it belongs to instead of a time
 * window. A shape moves to the last phase that had started when it was drawn.
 */
const migrateV2toV3: Migration = (input) => {
  const rawFrames = asArray(input['keyframes'], 'keyframes').map((raw) => {
    if (!isObject(raw)) throw new SchemaError('Expected keyframe object');
    return raw;
  });
  // v3 trusts array order as the running order, but v2 never guaranteed that —
  // only `t` was authoritative, and this is the last moment it exists. Sort a
  // copy before deriving anything from it, so both `times` (ownerAt's walk)
  // and the emitted `keyframes` agree on the real running order.
  const ordered = [...rawFrames].sort(
    (a, b) => asNumber(a['t'] ?? 0, 'keyframe.t') - asNumber(b['t'] ?? 0, 'keyframe.t'),
  );

  const times = ordered.map((raw) => ({
    id: asString(raw['id'], 'keyframe.id'),
    t: asNumber(raw['t'] ?? 0, 'keyframe.t'),
  }));

  const keyframes = ordered.map((raw) => {
    const { t: _t, ...rest } = raw;
    return rest;
  });

  const ownerAt = (appearsAt: number): string => {
    let owner = times[0]?.id ?? '';
    for (const frame of times) if (appearsAt >= frame.t) owner = frame.id;
    return owner;
  };

  const shapes = asArray(input['shapes'] ?? [], 'shapes').map((raw) => {
    if (!isObject(raw)) throw new SchemaError('Expected shape object');
    const { appearsAt, hidesAt: _hidesAt, ...rest } = raw;
    return { ...rest, keyframeId: ownerAt(typeof appearsAt === 'number' ? appearsAt : 0) };
  });

  return { ...input, schemaVersion: 3, keyframes, shapes };
};

const MIGRATIONS: Readonly<Record<number, Migration>> = {
  1: migrateV1toV2,
  2: migrateV2toV3,
};

/* ------------------------------------------------------------------ */
/* Parse / validate                                                    */
/* ------------------------------------------------------------------ */

function parseEntity(raw: unknown): Entity {
  if (!isObject(raw)) throw new SchemaError('Expected entity object');
  const entity: Entity = {
    id: asString(raw['id'], 'entity.id'),
    typeId: asString(raw['typeId'], 'entity.typeId'),
    label: typeof raw['label'] === 'string' ? raw['label'] : '',
    ...(typeof raw['teamId'] === 'string' ? { teamId: raw['teamId'] } : {}),
    ...(typeof raw['roleId'] === 'string' ? { roleId: raw['roleId'] } : {}),
    // Additive: a board without renamed positions has none.
    ...(typeof raw['roleLabel'] === 'string' && raw['roleLabel'] !== '' ? { roleLabel: raw['roleLabel'] } : {}),
  };
  return entity;
}

function parseShape(raw: unknown): Shape {
  if (!isObject(raw)) throw new SchemaError('Expected shape object');
  return {
    id: asString(raw['id'], 'shape.id'),
    kind: asShapeKind(raw['kind'], 'shape.kind'),
    points: asArray(raw['points'], 'shape.points').map((p, i) => asVec(p, `shape.points[${i}]`)),
    color: asString(raw['color'], 'shape.color'),
    width: asNumber(raw['width'], 'shape.width'),
    keyframeId: asString(raw['keyframeId'], 'shape.keyframeId'),
    // A lane names the piece it moves; an annotation names none. Optional, so
    // documents drawn before lanes were linked still load as annotations.
    ...(typeof raw['moverId'] === 'string' ? { moverId: raw['moverId'] } : {}),
    ...(Array.isArray(raw['carriedIds'])
      ? { carriedIds: raw['carriedIds'].filter((id): id is string => typeof id === 'string') }
      : {}),
    ...(typeof raw['fromId'] === 'string' ? { fromId: raw['fromId'] } : {}),
  };
}

function parseKeyframe(raw: unknown): Keyframe {
  if (!isObject(raw)) throw new SchemaError('Expected keyframe object');
  const rawPositions = raw['positions'];
  if (!isObject(rawPositions)) throw new SchemaError('Expected "keyframe.positions" to be an object');
  const positions: Record<string, Vec2> = {};
  for (const [entityId, value] of Object.entries(rawPositions)) {
    positions[entityId] = asVec(value, `keyframe.positions.${entityId}`);
  }
  return {
    id: asString(raw['id'], 'keyframe.id'),
    positions,
  };
}

function parseCurrent(input: Json): PlayDocument {
  const keyframes = asArray(input['keyframes'], 'keyframes').map(parseKeyframe);
  if (keyframes.length === 0) throw new SchemaError('A play must have at least one keyframe');
  const keyframeIds = new Set(keyframes.map((k) => k.id));

  // A shape naming no real phase loads without complaint but is visible at no
  // time and unreachable by the erase tool or clearShapes — adopt it into the
  // first phase rather than let it round-trip through autosave forever.
  const shapes = asArray(input['shapes'] ?? [], 'shapes')
    .map(parseShape)
    .map((shape) => (keyframeIds.has(shape.keyframeId) ? shape : { ...shape, keyframeId: keyframes[0]!.id }));

  return {
    schemaVersion: SCHEMA_VERSION,
    id: asString(input['id'], 'id'),
    name: asString(input['name'], 'name'),
    sportPackId: asString(input['sportPackId'], 'sportPackId'),
    createdAt: asNumber(input['createdAt'], 'createdAt'),
    updatedAt: asNumber(input['updatedAt'], 'updatedAt'),
    entities: asArray(input['entities'], 'entities').map(parseEntity),
    shapes,
    // No sort: phases carry no time, so the stored order is the running order.
    keyframes,
    // A view this build does not know is dropped, not rejected: it is only a
    // camera setting, and the board must still open.
    ...(isBoardView(input['view']) ? { view: input['view'] } : {}),
    // Additive: only a board that came from a saved play has one.
    ...(typeof input['playId'] === 'string' && input['playId'] !== '' ? { playId: input['playId'] } : {}),
  };
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export function serialize(doc: PlayDocument, pretty = false): string {
  return JSON.stringify(doc, null, pretty ? 2 : 0);
}

/** Runs the migration chain and validates. Throws `SchemaError` on bad input. */
export function migrate(input: unknown): PlayDocument {
  if (!isObject(input)) throw new SchemaError('Document must be a JSON object');

  let current: Json = input;
  let version = asNumber(current['schemaVersion'], 'schemaVersion');

  if (version > SCHEMA_VERSION) {
    throw new SchemaError(
      `Document schema v${version} is newer than this build (v${SCHEMA_VERSION}). Update the app to open it.`,
    );
  }

  while (version < SCHEMA_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) throw new SchemaError(`No migration registered from schema v${version}`);
    current = step(current);
    const next = asNumber(current['schemaVersion'], 'schemaVersion');
    if (next <= version) throw new SchemaError(`Migration from v${version} did not advance the version`);
    version = next;
  }

  return parseCurrent(current);
}

export function deserialize(json: string): PlayDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (error) {
    throw new SchemaError(`Not valid JSON: ${(error as Error).message}`);
  }
  return migrate(parsed);
}
